/**
 * The contact TITLE master (`/contact-titles`, owner 2026-10-18) as picker
 * suggestions.
 *
 * ⚠️ **A suggestion list, not a foreign key.** `d_contacts.title` stays free
 * text: an option's VALUE is the title string itself, the picker accepts a
 * typed title that is not on the list (`Combobox` `freeText`), and a stored
 * title the master does not hold is injected verbatim so it displays unchanged.
 *
 * One request is shared by every caller on a page (the contacts manager renders
 * a picker per row), cached briefly so a Catalogue edit shows up without a
 * reload. A failed read is not cached: the field still takes free text, so a
 * failure only means no suggestions.
 */

import { useEffect, useState } from 'react';
import { api } from './api';
import type { ComboboxOption } from '@/components/ui';

const TITLE_QUERY = { page: 1, perPage: 200 } as const;
const CACHE_MS = 60_000;

let cached: { at: number; promise: Promise<string[]> } | null = null;

function loadTitles(): Promise<string[]> {
  if (cached && Date.now() - cached.at < CACHE_MS) return cached.promise;
  const promise = api.listContactTitles(TITLE_QUERY).then((result) =>
    result.data
      .filter((record) => record.isActive)
      .map((record) => record.name.trim())
      .filter((name) => name !== ''),
  );
  const entry = { at: Date.now(), promise };
  cached = entry;
  promise.catch(() => {
    if (cached === entry) cached = null;
  });
  return promise;
}

export interface ContactTitlesState {
  /** Active title names in the master's own order. */
  titles: string[];
  loading: boolean;
}

export function useContactTitles(enabled = true): ContactTitlesState {
  const [titles, setTitles] = useState<string[]>([]);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    loadTitles()
      .then((names) => {
        if (alive) setTitles(names);
      })
      .catch(() => {
        if (alive) setTitles([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [enabled]);

  return { titles, loading: enabled ? loading : false };
}

/** The picker's options: every active title, plus the stored one when the list lacks it. */
export function contactTitleOptions(titles: string[], current: string): ComboboxOption[] {
  const options = titles.map((name) => ({ value: name, label: name }));
  const stored = current.trim();
  if (stored !== '' && !options.some((option) => option.value === current)) {
    options.unshift({ value: current, label: current });
  }
  return options;
}
